/**
 * QActionChips Component (v4.0 - Streamlined Actions)
 * Compact action chips with tight spacing
 *
 * v4.0 Updates (Feature Flagged):
 * - STREAMLINED_ACTIONS: true = 3 buttons (Signal, Analyze, Coach)
 * - STREAMLINED_ACTIONS: false = 6 buttons (original)
 * - No token costs displayed (removes friction anxiety)
 * - Feature flag for safe rollback
 *
 * v3.0:
 * - 6 action chips with distinct colored icons
 * - Compact grid layout - tighter spacing
 * - Smaller icons and labels
 * - Professional Bolt.new style
 */

import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SchoolIcon from '@mui/icons-material/School';
import { FEATURE_FLAGS } from '../../config/featureFlags';

// ═══════════════════════════════════════════════════════════════════════════
// ACTION CHIP CONFIGURATIONS (Feature Flagged)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * STREAMLINED: 3 core actions (Signal, Analyze, Coach)
 * Reduces decision fatigue, focuses on most-used actions
 */
const STREAMLINED_CHIPS = [
  {
    id: 'q_signal',
    icon: BoltIcon,
    label: 'Signal',
    color: '#F59E0B', // Amber/Yellow
    prompt: "Show me today's top trading signals with entry, SL, and TP levels."
  },
  {
    id: 'q_analyze',
    icon: BarChartIcon,
    label: 'Analyze',
    color: '#6366F1', // Indigo/Purple
    prompt: 'Analyze my EA performance and give me detailed statistics.'
  },
  {
    id: 'q_coach',
    icon: SchoolIcon,
    label: 'Coach',
    color: '#27B56D', // Green (brand)
    prompt: 'Give me personalized coaching to improve my trading results today.'
  }
];

/**
 * ORIGINAL: Full 6 action chips
 */
const ORIGINAL_CHIPS = [
  {
    id: 'q_scan',
    icon: SearchIcon,
    label: 'Q Scan',
    color: '#27B56D', // Green
    prompt: 'Scan my trading activity and identify patterns in my performance.'
  },
  {
    id: 'q_signal',
    icon: BoltIcon,
    label: 'Q Signal',
    color: '#F59E0B', // Amber/Yellow
    prompt: "Show me today's top trading signals with entry, SL, and TP levels."
  },
  {
    id: 'q_analyze',
    icon: BarChartIcon,
    label: 'Q Analyze',
    color: '#6366F1', // Indigo/Purple
    prompt: 'Analyze my EA performance and give me detailed statistics.'
  },
  {
    id: 'q_risk',
    icon: GppMaybeIcon,
    label: 'Q Risk',
    color: '#EF4444', // Red
    prompt: 'Assess my current risk exposure and suggest optimal position sizing.'
  },
  {
    id: 'q_review',
    icon: TrendingUpIcon,
    label: 'Q Review',
    color: '#10B981', // Teal
    prompt: 'Review my trading history and tell me how to improve.'
  },
  {
    id: 'q_improve',
    icon: LightbulbIcon,
    label: 'Q Improve',
    color: '#8B5CF6', // Purple/Violet
    prompt: 'Give me personalized recommendations to improve my trading results.'
  }
];

/**
 * Get action chips based on feature flag
 */
const getActionChips = () => {
  return FEATURE_FLAGS.STREAMLINED_ACTIONS ? STREAMLINED_CHIPS : ORIGINAL_CHIPS;
};

// Legacy export for backwards compatibility
const ACTION_CHIPS = ORIGINAL_CHIPS;

/**
 * QActionChips Component
 * @param {function} onAction - Callback when chip is clicked (receives action object)
 * @param {boolean} disabled - Disable all chips
 * @param {string} variant - 'horizontal' (row) or 'grid' (2x3 grid for panel view)
 */
const QActionChips = ({ onAction, disabled = false, variant = 'horizontal' }) => {
  const handleClick = (action) => {
    if (!disabled && onAction) {
      onAction(action);
    }
  };

  // Get chips based on feature flag
  const chips = getActionChips();
  const isGrid = variant === 'grid';
  const isStreamlined = FEATURE_FLAGS.STREAMLINED_ACTIONS;

  return (
    <Box
      sx={{
        display: 'grid',
        // Streamlined: always 3 columns, Original: 3 columns grid or auto-fit
        gridTemplateColumns: isStreamlined
          ? 'repeat(3, 1fr)'
          : (isGrid ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(100px, auto))'),
        gap: { xs: 0.75, sm: isStreamlined ? 1 : 0.75 },
        maxWidth: isStreamlined ? { xs: '280px', sm: '320px' } : (isGrid ? '280px' : 'none'),
        justifyItems: 'center'
      }}
    >
      {chips.map((action) => (
        <ActionChip
          key={action.id}
          action={action}
          onClick={() => handleClick(action)}
          disabled={disabled}
          variant={isStreamlined ? 'streamlined' : variant}
        />
      ))}
    </Box>
  );
};

/**
 * Individual Action Chip - Compact Bolt-style
 */
const ActionChip = ({ action, onClick, disabled, variant }) => {
  const IconComponent = action.icon;
  const isGrid = variant === 'grid';
  const isStreamlined = variant === 'streamlined';

  // Streamlined variant: Mobile-optimized cards for 3-button layout
  if (isStreamlined) {
    return (
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 0.75 },
          padding: { xs: '10px 6px', sm: '12px 8px' },
          backgroundColor: 'rgba(24, 24, 27, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: { xs: '8px', sm: '10px' },
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.15s ease',
          minWidth: { xs: '76px', sm: '88px' },
          flex: 1,
          '&:hover': !disabled ? {
            backgroundColor: 'rgba(39, 39, 42, 0.9)',
            borderColor: `${action.color}60`,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${action.color}20`
          } : {},
          '&:active': !disabled ? {
            transform: 'scale(0.97)'
          } : {},
          opacity: disabled ? 0.5 : 1
        }}
      >
        {/* Icon with colored background - responsive size */}
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: { xs: '6px', sm: '8px' },
            backgroundColor: action.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 3px 8px ${action.color}30`
          }}
        >
          <IconComponent sx={{ color: '#FFFFFF', fontSize: { xs: '1rem', sm: '1.125rem' } }} />
        </Box>
        {/* Label - responsive font */}
        <Typography
          sx={{
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            fontWeight: 600,
            color: '#F1F5F9',
            textAlign: 'center',
            lineHeight: 1.2,
            fontFamily: "'Inter', -apple-system, sans-serif"
          }}
        >
          {action.label}
        </Typography>
      </Box>
    );
  }

  // Grid variant: vertical card style - readable labels
  if (isGrid) {
    return (
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          padding: '8px 6px',
          backgroundColor: 'rgba(24, 24, 27, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '8px',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.15s ease',
          minWidth: '76px',
          '&:hover': !disabled ? {
            backgroundColor: 'rgba(39, 39, 42, 0.8)',
            borderColor: `${action.color}50`,
            transform: 'translateY(-1px)'
          } : {},
          '&:active': !disabled ? {
            transform: 'scale(0.98)'
          } : {},
          opacity: disabled ? 0.5 : 1
        }}
      >
        {/* Icon with colored background - compact */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            backgroundColor: action.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 6px ${action.color}25`
          }}
        >
          <IconComponent sx={{ color: '#FFFFFF', fontSize: '0.9375rem' }} />
        </Box>
        {/* Label - 13px readable font */}
        <Typography
          sx={{
            fontSize: '0.8125rem', // 13px - readable
            fontWeight: 600,
            color: '#E2E8F0', // Brighter text
            textAlign: 'center',
            lineHeight: 1.2,
            fontFamily: "'Inter', -apple-system, sans-serif"
          }}
        >
          {action.label}
        </Typography>
      </Box>
    );
  }

  // Horizontal variant: compact pill chip style
  return (
    <Chip
      onClick={onClick}
      disabled={disabled}
      icon={
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '5px',
            backgroundColor: action.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '4px !important'
          }}
        >
          <IconComponent sx={{ color: '#FFFFFF', fontSize: '0.8125rem' }} />
        </Box>
      }
      label={
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#E4E4E7' }}>
          {action.label}
        </Typography>
      }
      sx={{
        backgroundColor: 'rgba(24, 24, 27, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '6px',
        height: 32, // Smaller height
        paddingRight: '8px',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.12s ease',
        '&:hover': !disabled ? {
          backgroundColor: 'rgba(39, 39, 42, 0.8)',
          borderColor: `${action.color}40`
        } : {},
        '&:active': !disabled ? { transform: 'scale(0.98)' } : {},
        '&.Mui-disabled': { opacity: 0.5 },
        '& .MuiChip-icon': { marginRight: '4px' }
      }}
    />
  );
};

// Export the action chips config for use in useQChat
export { ACTION_CHIPS };
export default QActionChips;
